/**
 * =============================================================================
 * ActivityDetailsModalPremium
 * Versão: 1.1.0
 *
 * Modal especializado em ATIVIDADE (executar/visualizar) no contexto do
 * negócio, reaproveitando 100% dos componentes do DealDetailsModalPremium.
 *
 * Layout:
 *  - Header compacto com título do negócio + ações (fechar)
 *  - Stepper de fases em formato de SETAS (ActivityPhaseStepper)
 *  - Coluna esquerda: lista de atividades da fase (ActivityPhaseList)
 *  - Coluna central: painel de Roteiro do Playbook (ExecuteStepBody)
 *  - Coluna direita: timeline (DealActivitiesTab — reuso integral)
 *
 * Permissões:
 *  - usePermissions() (global) + useCrmPhasePermissions(workspaceId) (por fase)
 *
 * Backend:
 *  - useCrmDeal, useDealCadence, usePipelinePhaseStats — sem RPCs novas.
 * =============================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, ScrollText, History, MessageSquare, Phone, Mail, ExternalLink, ArrowUpRight } from 'lucide-react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { useCrmDeal } from '@/hooks/useCrmDeals';
import { useDealCadence } from '@/hooks/useDealCadence';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { usePermissions } from '@/hooks/usePermissions';
import { useCrmPhasePermissions } from '@/hooks/useCrmPhasePermissions';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';
import { formatCurrency } from '@/lib/currency-utils';
import { formatDealCode } from '@/lib/formatters';

import { ActivityPhaseStepper } from './ActivityPhaseStepper';
import { ActivityPhaseList } from './ActivityPhaseList';
import { ExecuteStepBody, type ExecuteStepDealContext } from '@/components/crm/deals/cadence/ExecuteStepBody';
import { DealActivitiesTab } from '@/components/crm/deals/modal/tabs/DealActivitiesTab';
import { ModalTabsBar, type ModalTabConfig } from '@/components/shared/ModalTabsBar';
import { SmartAvatar } from '@/components/ui/smart-avatar';

const logger = createLogger('ActivityDetailsModalPremium');

interface ActivityDetailsModalPremiumProps {
  dealId: string | null;
  /** Step/atividade ativa ao abrir (cadence step_id ou activity_id avulso) */
  activityId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** v1.1.0 — Callback para abrir o modal de detalhes do negócio */
  onOpenDeal?: (dealId: string) => void;
}

export function ActivityDetailsModalPremium({
  dealId,
  activityId,
  open,
  onOpenChange,
  onOpenDeal,
}: ActivityDetailsModalPremiumProps) {
  const isMobile = useIsMobile();
  const { profile, currentTenant } = useAuth();
  const { toast } = useToast();

  // ----- Permissões globais ---------------------------------------------------
  const { canView, canEdit } = usePermissions();
  const canViewDeals = canView('crm_deals');
  const canEditDeals = canEdit('crm_deals');

  // ----- Dados do negócio -----------------------------------------------------
  const { data: deal, isLoading: isDealLoading } = useCrmDeal(dealId);
  const workspaceId = (deal as any)?.pipeline?.workspace_id ?? null;

  // ----- Permissões granulares por fase --------------------------------------
  const phasePerms = useCrmPhasePermissions(workspaceId);

  // ----- Fases da pipeline (stepper) -----------------------------------------
  const { phases } = usePipelinePhaseStats(deal?.pipeline_id ?? null);

  // ----- Cadência (atividades por fase + roteiro) ----------------------------
  const { steps, isLoading: isCadenceLoading, completeStep, skipStep } = useDealCadence(dealId);

  // ----- Estado: fase selecionada + step ativo -------------------------------
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'geral' | 'historico'>('geral');

  // Inicializa quando abre / muda de deal
  useEffect(() => {
    if (!open || !deal) return;
    // Se uma atividade foi passada e ela existe nos steps → seleciona a fase dela
    const target = activityId ? steps.find((s) => s.step_id === activityId) : null;
    setSelectedPhaseId(target?.phase_id ?? deal.phase_id ?? null);
    setSelectedStepId(target?.step_id ?? null);
  }, [open, deal?.id, activityId, steps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quando troca de fase, seleciona o primeiro step pendente da fase
  const stepsInPhase = useMemo(() => {
    if (!selectedPhaseId) return steps;
    const filtered = steps.filter((s) => s.phase_id === selectedPhaseId || s.phase_id === null);
    return filtered.length ? filtered : steps;
  }, [steps, selectedPhaseId]);

  useEffect(() => {
    if (!stepsInPhase.length) {
      setSelectedStepId(null);
      return;
    }
    // Se step atual não está na fase selecionada → reseta para 1º pendente
    const stillThere = stepsInPhase.some((s) => s.step_id === selectedStepId);
    if (!stillThere) {
      const firstPending = stepsInPhase.find(
        (s) => s.step_status === 'pending' || s.step_status === 'in_progress',
      );
      setSelectedStepId(firstPending?.step_id ?? stepsInPhase[0].step_id);
    }
  }, [stepsInPhase, selectedStepId]);

  // ----- Step ativo & contexto de variáveis ----------------------------------
  const activeStep = stepsInPhase.find((s) => s.step_id === selectedStepId) ?? null;

  const dealContext: ExecuteStepDealContext = useMemo(
    () => ({
      dealId: deal?.id ?? null,
      contact: deal?.contact
        ? { name: deal.contact.name, phone: deal.contact.phone, email: deal.contact.email }
        : null,
      deal: deal
        ? {
            title: deal.title,
            value: deal.value as number | null,
            currency: deal.currency,
            phase: (deal as any).phase?.name ?? null,
            pipeline: deal.pipeline?.name ?? null,
          }
        : null,
      seller: { full_name: profile?.full_name ?? null },
      tenant: { name: currentTenant?.name ?? null },
    }),
    [deal, profile, currentTenant],
  );

  // ----- Permissões resolvidas para a fase selecionada -----------------------
  // Granular ON → usa matriz; OFF → usa permissões legadas globais.
  const canViewSelectedPhase = phasePerms.isGranularEnabled
    ? phasePerms.canDoOnPhase(selectedPhaseId, 'can_view')
    : canViewDeals;
  const canEditSelectedPhase = phasePerms.isGranularEnabled
    ? phasePerms.canDoOnPhase(selectedPhaseId, 'can_edit')
    : canEditDeals;

  // Helper para permissão de visualização de cada fase no stepper
  const canViewPhase = (phaseId: string) =>
    phasePerms.isGranularEnabled ? phasePerms.canDoOnPhase(phaseId, 'can_view') : canViewDeals;

  // ----- Handlers -------------------------------------------------------------
  const handleComplete = async (stepId: string) => {
    if (!canEditSelectedPhase) {
      toast({ title: 'Sem permissão', description: 'Você não pode concluir atividades nesta fase.', variant: 'destructive' });
      return;
    }
    try {
      await completeStep.mutateAsync(stepId);
      toast({ title: 'Atividade concluída' });
      logger.info('Atividade concluída', { stepId });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const handleSkip = async (stepId: string) => {
    if (!canEditSelectedPhase) return;
    try {
      await skipStep.mutateAsync({ stepId });
      toast({ title: 'Atividade pulada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const handleToggleComplete = (stepId: string, currentlyDone: boolean) => {
    if (currentlyDone) return; // re-abrir não é o foco deste modal
    void handleComplete(stepId);
  };

  // ----- Render ---------------------------------------------------------------
  const isLoading = isDealLoading || (isCadenceLoading && !steps.length);

  const dealValueLabel =
    deal?.value != null ? formatCurrency(Number(deal.value), { currency: deal.currency ?? 'BRL' }) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`p-0 flex flex-col overflow-hidden ${
          isMobile
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0'
            : 'max-w-[95vw] w-[95vw] h-[97vh]'
        }`}
        preventCloseOnOutsideInteraction
        hideCloseButton
      >
        <VisuallyHidden.Root>
          <DialogTitle>Detalhes da Atividade</DialogTitle>
        </VisuallyHidden.Root>

        <TooltipProvider>
          <div className="flex flex-col h-full min-h-0">
            {/* Header compacto */}
            <div className="flex items-center gap-3 px-4 h-12 border-b shrink-0">
              <div className="flex-1 min-w-0">
                {isLoading ? (
                  <Skeleton className="h-5 w-72" />
                ) : (
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Vendedor responsável (avatar + nome) antes do título */}
                    {((deal as any)?.assigned_user?.avatar_url || (deal as any)?.assigned_user?.full_name) && (
                      <span className="flex items-center gap-1.5 shrink-0">
                        <SmartAvatar
                          src={(deal as any)?.assigned_user?.avatar_url ?? undefined}
                          name={(deal as any)?.assigned_user?.full_name ?? '—'}
                          size="xs"
                          use3DFallback
                        />
                        <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                          {(deal as any)?.assigned_user?.full_name ?? '—'}
                        </span>
                        <span className="text-muted-foreground">·</span>
                      </span>
                    )}
                    {/* v1.1.0 — N.Negócio com link para abrir modal do negócio (antes do título) */}
                    {deal?.id && (
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground font-mono">N.{formatDealCode(deal.id)}</span>
                        {onOpenDeal && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0"
                            onClick={() => onOpenDeal(deal.id)}
                            title="Abrir detalhes do negócio"
                          >
                            <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-primary" />
                          </Button>
                        )}
                        <span className="text-muted-foreground">·</span>
                      </span>
                    )}
                    <h2 className="text-sm font-semibold truncate">{deal?.title ?? '—'}</h2>
                    {deal?.contact?.name && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground truncate">{deal.contact.name}</span>
                      </>
                    )}
                    {dealValueLabel && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-xs font-medium text-emerald-600">{dealValueLabel}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} aria-label="Fechar">
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Stepper de fases (setas) */}
            {!isLoading && phases.length > 0 && (
              <div className="border-b shrink-0 bg-muted/20">
                <ActivityPhaseStepper
                  phases={phases}
                  currentPhaseId={deal?.phase_id ?? null}
                  selectedPhaseId={selectedPhaseId}
                  onSelectPhase={(id) => {
                    setSelectedPhaseId(id);
                    logger.debug('Fase selecionada', { id });
                  }}
                  canViewPhase={canViewPhase}
                  sellerAvatar={null}
                  sellerName={null}
                />
              </div>
            )}

            {/* Barra de abas (Geral / Histórico) */}
            {!isLoading && (
              <ModalTabsBar<'geral' | 'historico'>
                tabs={[
                  { id: 'geral', label: 'Geral', icon: ScrollText },
                  { id: 'historico', label: 'Histórico', icon: History },
                ] as ModalTabConfig<'geral' | 'historico'>[]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}

            {/* Conteúdo das abas */}
            <div className="flex flex-1 min-h-0">
              {/* ABA GERAL: lista (esq.) + roteiro (centro larga) */}
              {activeTab === 'geral' && (
                <div className={`flex flex-1 min-h-0 ${isMobile ? 'flex-col' : 'flex-row'}`}>
                  <div
                    className={`${
                      isMobile ? 'border-b' : 'border-r w-[380px] shrink-0'
                    } flex flex-col min-h-0 max-h-[40vh] md:max-h-none`}
                  >
                    <div className="px-3 py-2 border-b shrink-0">
                      <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                        Próximas atividades
                      </h3>
                    </div>
                    {isLoading ? (
                      <div className="p-3 space-y-2">
                        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
                      </div>
                    ) : !canViewSelectedPhase ? (
                      <div className="p-6 text-center text-xs text-muted-foreground">
                        Sem permissão para visualizar atividades desta fase.
                      </div>
                    ) : (
                      <ActivityPhaseList
                        steps={steps}
                        phaseId={selectedPhaseId}
                        selectedStepId={selectedStepId}
                        onSelectStep={setSelectedStepId}
                        onToggleComplete={handleToggleComplete}
                        onSkip={handleSkip}
                        canComplete={canEditSelectedPhase}
                        isBusy={completeStep.isPending || skipStep.isPending}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                    {isLoading ? (
                      <div className="p-4 space-y-3">
                        <Skeleton className="h-6 w-1/2" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-24 w-full" />
                      </div>
                    ) : activeStep ? (
                      <ExecuteStepBody
                        step={activeStep}
                        context={dealContext}
                        isBusy={completeStep.isPending || skipStep.isPending}
                        canSkip={false}
                        hideFooter
                      />
                    ) : (
                      <NoPlaybookFallback
                        contactPhone={deal?.contact?.phone ?? null}
                        contactEmail={deal?.contact?.email ?? null}
                        contactName={deal?.contact?.name ?? null}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* ABA HISTÓRICO: timeline em largura total */}
              {activeTab === 'historico' && deal && (
                <div className="flex-1 min-h-0 overflow-hidden">
                  <DealActivitiesTab dealId={deal.id} isOpen isActive />
                </div>
              )}
            </div>
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Fallback exibido quando a atividade selecionada NÃO faz parte da cadência
// (atividade avulsa) — mostra atalhos rápidos com o telefone real do contato.
// ---------------------------------------------------------------------------
function NoPlaybookFallback({
  contactPhone,
  contactEmail,
  contactName,
}: {
  contactPhone: string | null;
  contactEmail: string | null;
  contactName: string | null;
}) {
  const phoneDigits = (contactPhone ?? '').replace(/\D+/g, '').replace(/^0+/, '');
  const waUrl = phoneDigits ? `https://wa.me/${phoneDigits}` : null;
  const telUrl = phoneDigits ? `tel:+${phoneDigits}` : null;
  const mailtoUrl = contactEmail ? `mailto:${contactEmail}` : null;
  const open = (u: string) => window.open(u, '_blank', 'noopener,noreferrer');

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-5">
      <div className="rounded-md border bg-muted/20 p-4">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
          <ScrollText className="h-3.5 w-3.5" />
          Sem roteiro de playbook
        </div>
        <p className="text-sm text-muted-foreground">
          Esta atividade não está vinculada a um step de Playbook de Vendas.
          Use os atalhos rápidos abaixo para executar a ação com o contato.
        </p>
      </div>

      <section>
        <div className="text-xs font-medium text-muted-foreground mb-2">Atalhos rápidos</div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="default"
            disabled={!waUrl}
            onClick={() => waUrl && open(waUrl)}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Abrir WhatsApp
            <ExternalLink className="h-3 w-3 opacity-70" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!telUrl}
            onClick={() => telUrl && open(telUrl)}
            className="gap-1.5"
          >
            <Phone className="h-3.5 w-3.5" />
            Ligar
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!mailtoUrl}
            onClick={() => mailtoUrl && open(mailtoUrl)}
            className="gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" />
            E-mail
          </Button>
        </div>
        {!phoneDigits && !contactEmail && (
          <p className="text-[11px] text-muted-foreground mt-2">
            Sem telefone/e-mail cadastrados {contactName ? `para ${contactName}` : 'para este contato'}.
          </p>
        )}
      </section>
    </div>
  );
}
